import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Sparkles, ExternalLink, X } from "lucide-react";
import { api } from "../lib/api";

/**
 * SVG-based force-radial graph for the Connect-the-Dots feature.
 * - Center "CONNECTION" node
 * - Subjects arrayed around it on a circle
 * - Hover = preview; Click = pin a detail panel with archive matches + deep-dive CTA
 */
export default function ConnectionGraph({ items = [], title = "" }) {
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);

  // When a node is selected, look up matching dossiers in the archive
  useEffect(() => {
    if (selected === null) {
      setMatches([]);
      return;
    }
    const subject = items[selected];
    if (!subject) return;
    setLoadingMatches(true);
    api
      .get("/dossiers", { params: { q: subject, limit: 3 }, timeout: 8000 })
      .then((r) => setMatches(r.data || []))
      .catch(() => setMatches([]))
      .finally(() => setLoadingMatches(false));
  }, [selected, items]);

  const active = selected ?? hover;

  const n = items.length;
  const width = 720;
  const height = 460;
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.min(width, height) / 2 - 90;

  const positions = items.map((label, i) => {
    const angle = (i / Math.max(n, 1)) * Math.PI * 2 - Math.PI / 2;
    return {
      label,
      i,
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
      angle,
    };
  });

  const trunc = (s, max = 22) => (s.length > max ? s.slice(0, max - 1) + "…" : s);

  const selectedLabel = selected !== null ? items[selected] : null;

  // If somehow we got an empty subjects array (older record, bad save, etc.)
  // hide the graph card entirely so we don't show a confusing blank box.
  if (!items || items.length === 0) return null;

  return (
    <div data-testid="connection-graph" className="bg-[#0a0a0a] border border-white/10 p-4 mb-8 no-print">
      <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-3 flex items-center justify-between">
        <span>LINKAGE MAP — {n} SUBJECTS</span>
        <span className="text-zinc-600">HOVER TO PREVIEW · CLICK TO PIN</span>
      </div>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        style={{ maxHeight: 520 }}
      >
        {/* Grid background */}
        <defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="1" />
          </pattern>
          <radialGradient id="centerGlow">
            <stop offset="0%" stopColor="#ff3b30" stopOpacity="0.4" />
            <stop offset="60%" stopColor="#ff3b30" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#ff3b30" stopOpacity="0" />
          </radialGradient>
          <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <rect x="0" y="0" width={width} height={height} fill="url(#grid)" pointerEvents="none" />

        {/* Outer ring */}
        <circle cx={cx} cy={cy} r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeDasharray="2 4" pointerEvents="none" />

        {/* Center glow */}
        <circle cx={cx} cy={cy} r={120} fill="url(#centerGlow)" pointerEvents="none" />

        {/* Lines from center to each node */}
        {positions.map((p) => {
          const isActive = active === p.i || active === null;
          return (
            <line
              key={`l-${p.i}`}
              x1={cx}
              y1={cy}
              x2={p.x}
              y2={p.y}
              stroke={active === p.i ? "#ff3b30" : "rgba(168, 85, 247, 0.45)"}
              strokeWidth={active === p.i ? 2 : 1}
              opacity={isActive ? 1 : 0.25}
              pointerEvents="none"
              style={{
                transition: "stroke 0.3s, stroke-width 0.3s, opacity 0.3s",
              }}
            />
          );
        })}

        {/* Subject-to-subject inter-links (pair every node with the next 2 to suggest the web) */}
        {n > 2 &&
          positions.map((p, i) => {
            const next = positions[(i + 1) % n];
            return (
              <line
                key={`il-${i}`}
                x1={p.x}
                y1={p.y}
                x2={next.x}
                y2={next.y}
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="1"
                strokeDasharray="3 3"
                pointerEvents="none"
              />
            );
          })}

        {/* Center node */}
        <g pointerEvents="none">
          <circle cx={cx} cy={cy} r={42} fill="#0F0F0F" stroke="#ff3b30" strokeWidth="2" filter="url(#softGlow)" />
          <text
            x={cx}
            y={cy - 4}
            textAnchor="middle"
            fontSize="9"
            fontFamily="monospace"
            fontWeight="700"
            letterSpacing="2"
            fill="#ff3b30"
          >
            CONNECTION
          </text>
          <text
            x={cx}
            y={cy + 10}
            textAnchor="middle"
            fontSize="8"
            fontFamily="monospace"
            letterSpacing="1.5"
            fill="rgba(255,255,255,0.55)"
          >
            {n} NODES
          </text>
        </g>

        {/* Subject nodes */}
        {positions.map((p) => {
          const isActive = active === p.i;
          const isSelected = selected === p.i;
          const labelOnLeft = p.x < cx;
          return (
            <g
              key={`n-${p.i}`}
              data-testid={`graph-node-${p.i}`}
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setHover(p.i)}
              onMouseLeave={() => setHover(null)}
              onClick={() => setSelected(isSelected ? null : p.i)}
            >
              {/* Invisible larger hit area for easier clicking */}
              <circle cx={p.x} cy={p.y} r={22} fill="transparent" />
              {/* Pulse ring for selected */}
              {isSelected && (
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={18}
                  fill="none"
                  stroke="#ff3b30"
                  strokeWidth="1.5"
                  opacity="0.5"
                >
                  <animate attributeName="r" values="14;22;14" dur="1.6s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.6;0.1;0.6" dur="1.6s" repeatCount="indefinite" />
                </circle>
              )}
              <circle
                cx={p.x}
                cy={p.y}
                r={isActive ? 12 : 8}
                fill={isActive ? "#ff3b30" : "#a855f7"}
                stroke="#0a0a0a"
                strokeWidth="3"
                style={{ transition: "r 0.2s, fill 0.2s" }}
              />
              {/* Number badge */}
              <text
                x={p.x}
                y={p.y + 3}
                textAnchor="middle"
                fontSize="8"
                fontFamily="monospace"
                fontWeight="700"
                fill="#0a0a0a"
                style={{ pointerEvents: "none" }}
              >
                {p.i + 1}
              </text>
              {/* Label */}
              <text
                x={labelOnLeft ? p.x - 16 : p.x + 16}
                y={p.y + 4}
                textAnchor={labelOnLeft ? "end" : "start"}
                fontSize="11"
                fontFamily="monospace"
                letterSpacing="0.5"
                fill={isActive ? "#ff3b30" : "#e4e4e7"}
                style={{ transition: "fill 0.2s", pointerEvents: "none" }}
              >
                {trunc(p.label)}
              </text>
            </g>
          );
        })}

        {/* Hover tooltip: full label when truncated, shown above the node */}
        {hover !== null && items[hover] && items[hover].length > 22 && (() => {
          const p = positions[hover];
          const full = items[hover];
          const padX = 8;
          const padY = 5;
          const charW = 6.2;
          const w = Math.min(360, full.length * charW + padX * 2);
          const h = 22;
          const tx = Math.max(w / 2 + 4, Math.min(width - w / 2 - 4, p.x));
          const ty = p.y - 22;
          return (
            <g style={{ pointerEvents: "none" }}>
              <rect
                x={tx - w / 2}
                y={ty - h}
                width={w}
                height={h}
                fill="#050505"
                stroke="#ff3b30"
                strokeWidth="1"
                rx="2"
              />
              <text
                x={tx}
                y={ty - h / 2 + 4}
                textAnchor="middle"
                fontSize="10"
                fontFamily="monospace"
                fill="#ff3b30"
              >
                {full.length > 56 ? full.slice(0, 55) + "…" : full}
              </text>
            </g>
          );
        })()}
      </svg>

      {/* Subject legend */}
      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5 font-mono text-[10px] tracking-wider">
        {items.map((s, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setSelected(selected === i ? null : i)}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            className={`text-left flex items-center gap-2 px-2 py-1 border transition-colors ${
              selected === i
                ? "border-[#ff3b30] text-[#ff3b30] bg-[#ff3b30]/10"
                : hover === i
                ? "border-[#ff3b30]/60 text-[#ff3b30] bg-[#ff3b30]/5"
                : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
            }`}
          >
            <span className="text-pink-400">{String(i + 1).padStart(2, "0")}</span>
            <span className="truncate">{s}</span>
          </button>
        ))}
      </div>

      {/* Selection detail panel */}
      {selected !== null && selectedLabel && (
        <div
          data-testid="graph-detail-panel"
          className="mt-4 border border-[#ff3b30]/40 bg-[#0F0F0F] p-5"
        >
          <div className="flex items-start justify-between gap-3 mb-3 pb-3 border-b border-white/10">
            <div className="min-w-0">
              <div className="font-mono text-[10px] tracking-[0.25em] text-[#ff3b30] mb-1.5">
                NODE {String(selected + 1).padStart(2, "0")} // SUBJECT FOCUS
              </div>
              <div className="font-heading font-black uppercase tracking-tight text-xl sm:text-2xl text-white leading-tight break-words">
                {selectedLabel}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSelected(null)}
              data-testid="graph-detail-close"
              className="shrink-0 text-zinc-500 hover:text-white border border-white/15 hover:border-white/40 px-2 py-1"
              aria-label="Close detail panel"
            >
              <X size={12} />
            </button>
          </div>

          {/* Archive matches */}
          <div className="mb-4">
            <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
              <Search size={11} /> ARCHIVE MATCHES
            </div>
            {loadingMatches ? (
              <div className="font-mono text-[10px] tracking-wider text-zinc-600">
                SCANNING VAULT...
              </div>
            ) : matches.length === 0 ? (
              <div className="font-mono text-[10px] tracking-wider text-zinc-600">
                NO DIRECT MATCH IN ARCHIVE.
              </div>
            ) : (
              <ul className="space-y-2">
                {matches.map((d) => (
                  <li key={d.slug}>
                    <Link
                      to={`/dossier/${d.slug}`}
                      data-testid={`graph-match-${d.slug}`}
                      className="group flex items-start justify-between gap-3 border border-white/10 hover:border-[#ff3b30]/50 hover:bg-[#141414] p-3 transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-600 mb-1">
                          FILE — {d.slug.toUpperCase()}
                        </div>
                        <div className="text-sm text-white font-heading font-black uppercase tracking-tight leading-tight mb-1 group-hover:text-[#ff3b30] transition-colors">
                          {d.title}
                        </div>
                        <p className="text-xs text-zinc-400 leading-relaxed line-clamp-2">
                          {d.summary}
                        </p>
                      </div>
                      <ExternalLink
                        size={12}
                        className="text-zinc-500 group-hover:text-[#ff3b30] mt-1 shrink-0 transition-colors"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* CTAs */}
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/deep-dive?topic=${encodeURIComponent(selectedLabel)}`}
              data-testid="graph-deep-dive-cta"
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00ff88]/50 text-[#00ff88] hover:bg-[#00ff88]/10 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors"
            >
              <Sparkles size={11} /> Deep Dive on this
            </Link>
            <Link
              to={`/archive?q=${encodeURIComponent(selectedLabel)}`}
              data-testid="graph-archive-cta"
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 text-zinc-400 hover:text-white hover:border-white/40 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors"
            >
              <Search size={11} /> Search Archive
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
